import type WebApiClient from "../../api/WebApiClient.js";
import {
  LOAD_PROTEIN_FAILURE,
  LOAD_PROTEIN_REQUEST,
  LOAD_PROTEIN_SUCCESS,
  UPDATE_LAST_OPEN_PROTEIN_FEATURE_TABLE_SPEC,
} from "../action-types.js";
import { type IAppState } from "../reducer.js";
import { createReducer } from "../redux-utils.js";
import { createErrorHandler } from "@/app-utils/errors.ts";
import { type InputTableQuerySpec, type Protein } from "@/graphql.ts";

/**
 * State definition
 */

export interface IProteinState {
  protein?: Protein;
  isLoading: boolean;
  lastOpenFeatureTableSpec: InputTableQuerySpec;
}

const initialState: IProteinState = {
  isLoading: false,
  protein: null,
  lastOpenFeatureTableSpec: null,
};

/**
 * Reducers
 */
export default createReducer(initialState, {
  [LOAD_PROTEIN_REQUEST]: (state: IProteinState, _payload: Protein): IProteinState => {
    return { ...state, isLoading: true };
  },
  [LOAD_PROTEIN_SUCCESS]: (state: IProteinState, payload: Protein): IProteinState => {
    return { ...state, isLoading: false, protein: payload };
  },
  [LOAD_PROTEIN_FAILURE]: (state: IProteinState, _payload): IProteinState => {
    return { ...state, isLoading: false, protein: null };
  },
  [UPDATE_LAST_OPEN_PROTEIN_FEATURE_TABLE_SPEC]: (
    state: IProteinState,
    payload: InputTableQuerySpec,
  ): IProteinState => {
    return {
      ...state,
      lastOpenFeatureTableSpec: payload,
    };
  },
});

/**
 * Action creators
 */
let lastFetchedProteinId = -1;

export function shouldLoadProtein(proteinId: number) {
  return (dispatch, getState, webApiClient: WebApiClient): Promise<Protein> => {
    const state: IAppState = getState();
    lastFetchedProteinId = proteinId;

    // fetch the protein only if no loading is currently in progress and protein is either absent or doesn't match the protein ID
    if (!state.protein.protein || state.protein.protein.id !== proteinId) {
      dispatch({ type: LOAD_PROTEIN_REQUEST });

      return webApiClient
        .fetchProtein(proteinId)
        .then((response: Protein) => {
          if (lastFetchedProteinId === response.id) {
            dispatch({
              type: LOAD_PROTEIN_SUCCESS,
              payload: response,
            });

            return response;
          }

          return state.protein.protein;
        })
        .catch(
          createErrorHandler(dispatch, {
            actionType: LOAD_PROTEIN_FAILURE,
            alertTitle: "Fetching of Protein Failed",
          }),
        );
    } else {
      return Promise.resolve(state.protein.protein);
    }
  };
}

export function updateLastOpenProteinFeatureTableSpec(tableSpec: InputTableQuerySpec) {
  return (dispatch, _getState) => {
    if (tableSpec) {
      dispatch({ type: UPDATE_LAST_OPEN_PROTEIN_FEATURE_TABLE_SPEC, payload: tableSpec });
    }
  };
}
